import { computed, ref, type ComputedRef } from 'vue'

import { definitionsMetadataApi, type DefinitionsMetadataApi } from '../api/DefinitionsMetadataApi'
import {
  SaveGroupingFlow,
  type CustomGroupFailure,
  type GroupingValidationError,
} from '../flows/SaveGroupingFlow'
import type { Grouping, GroupingDraft } from '../models/grouping.types'
import { ALLOWED_GROUPING_LEVELS, levelHasFreeSlot, type GroupingCaps } from '../service/groupRules'
import { useDefinitionsStore } from '../stores/definitionsStore'

import { useAccount, useWorkspace } from '@/features/account'
import { useLevelMetadata, type ReportLevel } from '@/features/reports'
import type { UiAsyncState } from '@/shared/ui'

export interface GroupingSaveOutcome {
  ok: boolean
  error?: GroupingValidationError
  failed: CustomGroupFailure[]
}

export interface UseGroupings {
  groupings: ComputedRef<Grouping[]>
  /** Every level a grouping may live on, whether or not it still has a free slot. */
  allowedLevels: ComputedRef<ReportLevel[]>
  /** Levels with a free slot — what the create form may offer. */
  selectableLevels: ComputedRef<ReportLevel[]>
  /** Levels already at their cap, named in the form's hint (`GroupModal.vue:17-19`). */
  disabledLevels: ComputedRef<ReportLevel[]>
  levelName: (level: string) => string
  dimensionsForLevel: (level: string) => ReportLevel['tableDimensions']
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<unknown>
  pending: ComputedRef<boolean>
  reload: () => Promise<void>
  save: (draft: GroupingDraft, original?: Grouping) => Promise<GroupingSaveOutcome>
  remove: (grouping: Grouping) => Promise<boolean>
}

export function useGroupings(api: DefinitionsMetadataApi = definitionsMetadataApi): UseGroupings {
  const account = useAccount()
  const workspace = useWorkspace()
  // AC: the level vocabulary is 08's, not v3's `columnStore.formatLevels()` (`Grouping.vue:125`).
  const levels = useLevelMetadata()
  const store = useDefinitionsStore()
  const loading = ref(false)
  const pending = ref(false)
  const loadError = ref<unknown>(null)
  /**
   * `null` until the workspace's capacities have been read — and after a failed read, which is
   * deliberate: an unknown ceiling must not become an invented one. See `capForLevel`.
   */
  const caps = ref<GroupingCaps>(null)

  function scope(): { accountId: number; workspaceId: number | string } | null {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    return accountId === undefined || workspaceId === undefined ? null : { accountId, workspaceId }
  }

  const allowedLevels = computed(() =>
    levels.levels.value.filter((level) => ALLOWED_GROUPING_LEVELS.includes(level.level)),
  )

  function countFor(level: string): number {
    return store.groupings.filter((grouping) => grouping.entity_type === level).length
  }

  const selectableLevels = computed(() =>
    allowedLevels.value.filter((level) =>
      levelHasFreeSlot(caps.value, level.level, countFor(level.level)),
    ),
  )
  const disabledLevels = computed(() =>
    allowedLevels.value.filter(
      (level) => !levelHasFreeSlot(caps.value, level.level, countFor(level.level)),
    ),
  )

  async function reload(): Promise<void> {
    const context = scope()
    if (!context) return
    loading.value = true
    loadError.value = null
    try {
      // The capacity read is a SEPARATE settle: it is configuration, not the user's data, so a
      // failure there must not blank the list. An unread ceiling simply stays unknown.
      const [groupings, capacities] = await Promise.allSettled([
        api.listGroupings(context.accountId, context.workspaceId),
        api.groupingCapacities(context.accountId, context.workspaceId),
      ])
      if (capacities.status === 'fulfilled') caps.value = capacities.value?.capacities ?? null
      if (groupings.status === 'rejected') throw groupings.reason
      store.setGroupings(groupings.value)
    } catch (cause) {
      loadError.value = cause
    } finally {
      loading.value = false
    }
  }

  async function save(draft: GroupingDraft, original?: Grouping): Promise<GroupingSaveOutcome> {
    const context = scope()
    if (!context) return { ok: false, failed: [] }
    pending.value = true
    loadError.value = null
    try {
      const result = await new SaveGroupingFlow({ api }).save({
        draft,
        accountId: context.accountId,
        workspaceId: context.workspaceId,
        ...(original ? { original } : {}),
      })
      if (!result.ok) return { ok: false, error: result.error, failed: [] }
      // v3 `GroupModal.complete()` (`:259`) force-refetched the workspace attribute blob after
      // every grouping write — `custom_grouping` is backend-derived from this API's records, and
      // only that re-fetch picks up the change. 08's group selectors (`useSectionGroups`) read the
      // cached attribute, so skipping this leaves them showing stale groups until an unrelated
      // reload happens to refresh it.
      await Promise.all([reload(), workspace.refreshAttributes()])
      return { ok: true, failed: result.failed }
    } catch (cause) {
      // The grouping write itself failed; the real error reaches the UI untouched (D-30-4).
      loadError.value = cause
      return { ok: false, failed: [] }
    } finally {
      pending.value = false
    }
  }

  async function remove(grouping: Grouping): Promise<boolean> {
    const context = scope()
    if (!context || grouping.id === undefined) return false
    pending.value = true
    loadError.value = null
    try {
      await api.deleteGrouping(context.accountId, context.workspaceId, grouping.id)
      // Same v3 parity as `save` (`Grouping.vue:172,195`): a deleted grouping's slot must drop out
      // of the cached `custom_grouping` attribute too, not just this module's own store.
      await Promise.all([reload(), workspace.refreshAttributes()])
      return true
    } catch (cause) {
      loadError.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  return {
    groupings: computed(() => store.groupings),
    allowedLevels,
    selectableLevels,
    disabledLevels,
    levelName: (level: string) =>
      allowedLevels.value.find((entry) => entry.level === level)?.name ?? level,
    dimensionsForLevel: (level: string) =>
      levels.levels.value.find((entry) => entry.level === level)?.tableDimensions ?? [],
    state: computed<UiAsyncState>(() => {
      if (loading.value) return 'loading'
      if (loadError.value !== null) return 'error'
      return store.groupings.length > 0 ? 'ready' : 'empty'
    }),
    error: computed(() => loadError.value),
    pending: computed(() => pending.value),
    reload,
    save,
    remove,
  }
}
